import React, { useEffect, useState } from 'react';

import { trackConversion, trackFunnelStage } from '../utils/analytics';
import { trackEvent } from '../utils/marketing';
import { FunnelContext, FunnelStage, useFunnel } from './FunnelContext';

// Define props for the ConversionFunnel component
interface ConversionFunnelProps {
  initialStage?: FunnelStage;
  onStageChange?: (stage: FunnelStage) => void;
  children: React.ReactNode;
  trackingId?: string;
  metadata?: Record<string, unknown>;
}

/**
 * ConversionFunnel Component
 *
 * A wrapper component that tracks user progression through a marketing funnel.
 * It automatically logs analytics events when users move between funnel stages
 * and provides context to child components about the current funnel stage.
 */
const ConversionFunnel: React.FC<ConversionFunnelProps> = ({
  initialStage = FunnelStage.AWARENESS,
  onStageChange,
  children,
  trackingId,
  metadata = {},
}) => {
  const [currentStage, setCurrentStage] = useState<FunnelStage>(initialStage);
  const [funnelData, setFunnelData] = useState<Record<string, unknown>>({
    startTime: new Date().toISOString(),
    ...metadata,
  });

  // Track initial stage view on component mount
  useEffect(() => {
    trackFunnelStage(initialStage, {
      trackingId,
      ...metadata,
    });
  }, [initialStage, trackingId, metadata]);

  // Function to advance to the next funnel stage
  const advanceStage = (nextStage: FunnelStage, additionalData?: Record<string, unknown>) => {
    if (nextStage !== currentStage) {
      // Update funnel data with time spent in previous stage
      const updatedFunnelData = {
        ...funnelData,
        [`${currentStage}CompletedAt`]: new Date().toISOString(),
        ...additionalData,
      };

      // Track the stage change in analytics
      trackFunnelStage(nextStage, {
        previousStage: currentStage,
        trackingId,
        timeInPreviousStage: calculateTimeInStage(currentStage, updatedFunnelData),
        ...additionalData,
      });

      // Also track in marketing tools
      trackEvent('funnel_stage_change', {
        from: currentStage,
        to: nextStage,
        trackingId,
        ...additionalData,
      });

      // Update state
      setCurrentStage(nextStage);
      setFunnelData(updatedFunnelData);

      // Call the onStageChange callback if provided
      if (onStageChange) {
        onStageChange(nextStage);
      }
    }
  };

  // Track conversion events
  const trackFunnelConversion = (
    conversionName: string,
    value?: number,
    additionalData?: Record<string, unknown>
  ) => {
    const conversionData = {
      funnelStage: currentStage,
      trackingId,
      ...funnelData,
      ...additionalData,
    };

    // Track in analytics
    trackConversion(conversionName, value, currentStage);

    // Also track in marketing tools
    trackEvent(conversionName, {
      value,
      funnelStage: currentStage,
      trackingId,
      ...additionalData,
    });

    return conversionData;
  };

  // Helper function to calculate time spent in a stage
  const calculateTimeInStage = (
    stage: FunnelStage,
    data: Record<string, unknown>
  ): number | undefined => {
    const stageCompletedAt = data[`${stage}CompletedAt`];
    const previousStageCompletedAt = getPreviousStageCompletedTime(stage, data);

    if (stageCompletedAt && previousStageCompletedAt) {
      const startTime = new Date(previousStageCompletedAt as string).getTime();
      const endTime = new Date(stageCompletedAt as string).getTime();
      return Math.floor((endTime - startTime) / 1000); // Time in seconds
    }

    return undefined;
  };

  // Helper to get the completion time of the previous stage
  const getPreviousStageCompletedTime = (
    currentStage: FunnelStage,
    data: Record<string, unknown>
  ): string | undefined => {
    const stages = Object.values(FunnelStage);
    const currentIndex = stages.indexOf(currentStage);

    if (currentIndex <= 0) {
      return data.startTime as string;
    }

    const previousStage = stages[currentIndex - 1];
    return data[`${previousStage}CompletedAt`] as string;
  };

  // Create context value to pass to children
  const funnelContextValue = {
    currentStage,
    advanceStage,
    trackConversion: trackFunnelConversion,
    funnelData,
  };

  // Provide the FunnelContext to children
  return <FunnelContext.Provider value={funnelContextValue}>{children}</FunnelContext.Provider>;
};

// Example usage component
export const FunnelStageButton: React.FC<{
  stage: FunnelStage;
  children: React.ReactNode;
  onClick?: () => void;
}> = ({ stage, children, onClick }) => {
  const { advanceStage } = useFunnel();

  const handleClick = () => {
    advanceStage(stage);
    if (onClick) onClick();
  };

  return <button onClick={handleClick}>{children}</button>;
};

export default ConversionFunnel;
